import React from 'react';
import {
  List as MuiList,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemButton,
  Typography,
  Box,
  Chip,
  Avatar,
  Skeleton,
  Paper,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';

interface ListProps {
  children: React.ReactNode;
  dense?: boolean;
  sx?: SxProps<Theme>;
}

export const List: React.FC<ListProps> = ({ children, dense = false, sx }) => {
  return (
    <MuiList dense={dense} sx={sx}>
      {children}
    </MuiList>
  );
};

interface ListItemProps {
  primary: string;
  secondary?: string;
  icon?: React.ReactNode;
  avatar?: string;
  action?: React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export const ListItemComponent: React.FC<ListItemProps> = ({
  primary,
  secondary,
  icon,
  avatar,
  action,
  onClick,
  selected = false,
  disabled = false,
  sx,
}) => {
  const content = (
    <>
      {(icon || avatar) && (
        <ListItemIcon>
          {avatar ? (
            <Avatar src={avatar} sx={{ width: 40, height: 40 }}>
              {primary.charAt(0).toUpperCase()}
            </Avatar>
          ) : (
            icon
          )}
        </ListItemIcon>
      )}
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{
          fontWeight: selected ? 600 : 400,
        }}
      />
      {action && (
        <ListItemSecondaryAction>
          {action}
        </ListItemSecondaryAction>
      )}
    </>
  );

  if (onClick) {
    return (
      <ListItemButton
        onClick={onClick}
        selected={selected}
        disabled={disabled}
        sx={sx}
      >
        {content}
      </ListItemButton>
    );
  }

  return (
    <ListItem selected={selected} disabled={disabled} sx={sx}>
      {content}
    </ListItem>
  );
};

interface StatusListItemProps extends Omit<ListItemProps, 'action'> {
  status: 'success' | 'warning' | 'error' | 'info' | 'pending';
  statusText: string;
  timestamp?: string;
  amount?: number;
  currency?: string;
}

export const StatusListItem: React.FC<StatusListItemProps> = ({
  primary,
  secondary,
  icon,
  avatar,
  status,
  statusText,
  timestamp,
  amount,
  currency = '€',
  onClick,
  selected = false,
  disabled = false,
  sx,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const action = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {amount !== undefined && (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {currency}{amount.toFixed(2)}
        </Typography>
      )}
      <Chip
        label={statusText}
        color={getStatusColor(status) as any}
        size="small"
        variant={status === 'pending' ? 'outlined' : 'filled'}
      />
    </Box>
  );

  // Crea una stringa per il secondary text
  const secondaryText = [
    secondary,
    timestamp ? new Date(timestamp).toLocaleString('it-IT') : null
  ].filter(Boolean).join(' • ');

  return (
    <ListItemComponent
      primary={primary}
      secondary={secondaryText}
      icon={icon}
      avatar={avatar}
      action={action}
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      sx={sx}
    />
  );
};

interface EmptyListProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const EmptyList: React.FC<EmptyListProps> = ({
  title,
  description,
  icon,
  action,
  sx,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: 'center',
        bgcolor: 'grey.50',
        borderRadius: 2,
        ...sx,
      }}
    >
      {icon && (
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          {icon}
        </Box>
      )}
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
      {action}
    </Paper>
  );
};

interface LoadingListProps {
  count?: number;
  dense?: boolean;
}

export const LoadingList: React.FC<LoadingListProps> = ({ count = 5, dense = false }) => {
  return (
    <List dense={dense}>
      {Array.from({ length: count }).map((_, index) => (
        <ListItem key={index}>
          <ListItemIcon>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemIcon>
          <ListItemText
            primary={<Skeleton variant="text" width="60%" />}
            secondary={<Skeleton variant="text" width="40%" />}
          />
          <ListItemSecondaryAction>
            <Skeleton variant="rectangular" width={80} height={24} />
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
};

interface ListSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const ListSection: React.FC<ListSectionProps> = ({
  title,
  subtitle,
  children,
  action,
  sx,
}) => {
  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
};

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  searchValue,
  onSearchChange,
  filters,
  actions,
  sx,
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <TextField
            fullWidth
            placeholder="Cerca..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>
        {filters && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {filters}
          </Box>
        )}
        {actions && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {actions}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
